// @givmo/app-admin — Settings → Payment Methods → Add card dialog (07-1.3).
//
// Replaces the legacy Flutter `add_card_pop_up.dart` + its `web_view_handler.dart` Shift4 IFRAME.
// The card fields now render inside `@givmo/payments` `CardForm` — Shift4's OWN PCI Components
// iframes (the SAQ-A mechanism), loaded from the Shift4 CDN via `useShift4`. This wrapper never sees
// a raw PAN/CVC and only ever handles the opaque `token.id`.
//
// Flow (VAULT ONLY — this dialog does NOT charge the card; charging is the separate 07-1.2 slice):
//   1. On open, source the Shift4 publishable key from the backend config endpoint (`usePaymentConfig`)
//      and instantiate Shift4 (`useShift4`). Show a loader until ready; a config/instantiation failure
//      shows an inline "unavailable" note (never a baked key).
//   2. `CardForm` tokenizes on submit → `onToken(token)`.
//   3. The opaque `token.id` is sent to `POST /v2/admin/company/wallet/cards` (create → vault) via the
//      generated `companiesCreateCompanyCardV2Mutation` (body `{ card_token }`). On success: invalidate
//      the cards list, toast, close. A Shift4 field/tokenization error or a create 4xx/5xx surfaces
//      inline (generic copy; the raw error is never rendered).
//
// SECURITY: the token is never logged; card data lives only inside Shift4's iframes. No charge path is
// wired here (no deposit/approve call) — this is card vaulting + management only.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  companiesCreateCompanyCardV2Mutation,
  companiesGetCompanyCardsV2QueryKey,
} from '@givmo/api-client/query';
import { isGivmoError, supportReference } from '@givmo/api-client';
import { CardForm, useShift4, type Shift4Token } from '@givmo/payments';
import { Button, Dialog, testIdProp, toast } from '@givmo/design-system';
import { usePaymentConfig } from './use-payment-config';

const PREFIX = 'payment-methods-add';

const GENERIC_ERROR = 'Could not add your card. Please check the details and try again.';
const UNAVAILABLE =
  'Secure card entry is temporarily unavailable. Please try again in a few moments.';

export interface AddCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddCardDialog({ open, onOpenChange }: AddCardDialogProps) {
  const queryClient = useQueryClient();

  // Source the publishable key + instantiate Shift4 only while the dialog is open (don't load the
  // Shift4 CDN script on the settings page at rest).
  const { publishableKey, isLoading: configLoading, isError: configError } = usePaymentConfig(open);
  const { shift4, status } = useShift4(publishableKey);

  const [error, setError] = useState<string | null>(null);
  const [errorReference, setErrorReference] = useState<string | null>(null);

  // Reset the inline error whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setError(null);
      setErrorReference(null);
    }
  }, [open]);

  const createMutation = useMutation({
    ...companiesCreateCompanyCardV2Mutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: companiesGetCompanyCardsV2QueryKey() });
      toast.success('Card added');
      onOpenChange(false);
    },
    onError: (err) => {
      // Generic copy only — the raw processor/validation error is never surfaced verbatim.
      setError(isGivmoError(err) ? err.message : GENERIC_ERROR);
      setErrorReference(isGivmoError(err) ? supportReference(err) : null);
    },
  });

  const submitting = createMutation.isPending;

  const handleToken = (token: Shift4Token) => {
    setError(null);
    setErrorReference(null);
    // The opaque token id is the ONLY card-derived value that leaves the iframe. Never logged.
    createMutation.mutate({ body: { card_token: token.id } });
  };

  const handleCardError = () => {
    // A Shift4 field/tokenization error — generic copy (the raw message can leak entry details, and
    // is never rendered or logged).
    setError(GENERIC_ERROR);
    setErrorReference(null);
  };

  // The card entry is "unavailable" when the config couldn't be sourced or Shift4 failed to load.
  const unavailable = configError || status === 'error';
  const loadingEntry = !unavailable && (configLoading || shift4 === null);

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <Dialog.Content aria-label="Add a card" className="flex flex-col gap-16" testId={PREFIX}>
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Add a card
        </Dialog.Title>
        <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          Your card details are entered securely and are never stored on Givmo’s servers.
        </Dialog.Description>

        {unavailable ? (
          <p
            className="font-sans text-s14 font-w400 tracking-normal text-text-tertiary"
            {...testIdProp(`${PREFIX}-unavailable`)}
          >
            {UNAVAILABLE}
          </p>
        ) : loadingEntry ? (
          <p
            className="font-sans text-s14 font-w400 tracking-normal text-text-tertiary"
            {...testIdProp(`${PREFIX}-loading`)}
          >
            Loading secure card entry…
          </p>
        ) : (
          // `shift4` is non-null here (guarded by loadingEntry above).
          <CardForm
            shift4={shift4!}
            onToken={handleToken}
            onError={handleCardError}
            busy={submitting}
            submitLabel={submitting ? 'Adding…' : 'Add card'}
            submitButtonProps={{
              appearance: 'saas',
              tone: 'primary',
              size: 'md',
              className: 'w-full',
            }}
            testId={`${PREFIX}-form`}
            className="flex flex-col gap-12"
          />
        )}

        {error !== null && (
          <div className="flex flex-col gap-4">
            <p
              className="font-sans text-s14 font-w500 tracking-normal text-danger-700"
              {...testIdProp(`${PREFIX}-error`)}
            >
              {error}
            </p>
            {errorReference !== null && (
              <p
                className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary"
                {...testIdProp(`${PREFIX}-error-reference`)}
              >
                {errorReference}
              </p>
            )}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end">
          <Dialog.Close
            render={<Button appearance="saas" tone="secondary" size="md" disabled={submitting} />}
            {...testIdProp(`${PREFIX}-cancel`)}
          >
            Cancel
          </Dialog.Close>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
